import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import {
  additionalServiceByGroup,
  additionalServiceByOrganization,
} from "../../graphql/queries";
import defaultAdditionalServices from "../../shared/additional-services";
import { centsToLocalString } from "../../shared/money";
import { AppContext } from "../../contextProviders/AppContext";
import GroupedService from "../../components/GroupedService";
import PriceSummary from "../../components/PriceSummary";
import {
  Button,
  Grid,
  Typography,
  useTheme,
  Stack,
  TextField,
  FormControl,
  FormLabel,
  InputAdornment,
  Divider,
  Switch,
} from "@mui/material";
import { motion } from "framer-motion";
import { pageTransition } from "../../animation-variants";
import { hideAdditionalServicesTitle } from "../../features";
import { Box } from "@mui/system";

import ServiceImage from "../../components/AdditionalServiceImage";
import { translation } from "../../components/Dictionary";
import { Check } from "@mui/icons-material";
import { captureError, getServiceDescription, listAll } from "../../utilities";
import { gtmAddServiceToCart, gtmRemoveServiceFromCart } from "../../gtm";
export default function AdditionalServices() {
  const {
    activeStep,
    additionalServiceOffers,
    cart,
    handleActiveStep,
    setAdditionalServiceOffers,
    selectedAdditionalServices,
    selectedCompany,
    language,
    setSelectedAdditionalServices,
    mobileViewPort,
  } = useContext(AppContext);

  const theme = useTheme();
  const FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE = useMemo(() => {
    if (selectedCompany?.group) {
      return hideAdditionalServicesTitle(selectedCompany?.group);
    }
  }, [selectedCompany]);

  const additionalServiceMoreInfo = false;
  const [groupedDeductibles, setGroupedDeductibles] = useState(null);
  const handleAdvance = () => {
    if (exportLicenseError) {
      return;
    }
    handleActiveStep(activeStep + 1);
    return <Navigate to="/reservation" />;
  };

  // All items in cart share same attributes for reservations, so we can use attributes of first in cart
  // (besides vehicle and freeTimes, freeTimes are not used on /reservation)

  const exportLicenseError = useMemo(() => {
    const exportLicense = selectedAdditionalServices?.find(
      (c) => c.id === "exportLicense" || c.type === "EXPORT_LICENSE"
    );
    // exportLicense must have details specified
    if (exportLicense && !exportLicense?.details) return true;
    return false;
  }, [selectedAdditionalServices]);

  function updateDetails(id, details) {
    const services = selectedAdditionalServices.map((s) => {
      if (s.id === id) {
        return { ...s, details: details };
      } else {
        return s;
      }
    });
    setSelectedAdditionalServices(services);
  }

  const [checked, setChecked] = useState(true);

  const handleChangePremium = (event) => {
    console.log(event.target);
    setChecked(!event.target.checked);
  };

  const getPriceMultiplier = (service) => {
    if (["deductible", "distance"].includes(service?.category)) {
      return cart.reduce((t, vehicle) => t + vehicle.quantity, 0);
    }
    return 1;
  };

  const serviceQuantity = (id) => {
    const isItemInCart = selectedAdditionalServices.find(
      (item) => item.id === id
    );
    if (!isItemInCart) return 0;
    return isItemInCart.quantity;
  };

  /*  
   The additional services are fetched when the page loads. 
   They include the information of the service that is not in the vehicle object.
   */

  const fetchAdditionalServiceData = useCallback(async () => {
    let response = null;
    const { group, organizationId, orgBusinessId } = selectedCompany;
    try {
      if (group) {
        response = await listAll(
          graphqlOperation(additionalServiceByGroup, { group: group }),
          "additionalServiceByGroup"
        );
      }
      if (organizationId && orgBusinessId) {
        response = await listAll(
          graphqlOperation(additionalServiceByOrganization, {
            organizationId: organizationId,
          }),
          "additionalServiceByOrganization"
        );
      }

      // add price for the services from the selected offer (product in the cart)
      // filter out services that are removed from the dealer-app (_removed)
    } catch {
      captureError(
        "fetchAdditionalServices missing group or orgBusinessId",
        "CUSTOMER_SERVICE_MISSING_PARAMETER",
        ""
      );
    }
    if (response) {
      // add default additional services to the services from the response
      const additionalServicesCombined = [
        ...defaultAdditionalServices,
        ...response,
      ];

      const availableOffers = [];

      // loop through all default services to see what are available for the offer
      additionalServicesCombined.forEach((additionalService) => {
        cart.forEach((item) => {
          // check if service is included in the offer & get the offer price for it
          const service = item?.additionalServices.find(
            (s) => s.key === additionalService.id
          );
          // handle organization users: filter out services from other businessess
          if ((organizationId && service) || (orgBusinessId && service)) {
            if (
              additionalService?.orgBusinessId === orgBusinessId ||
              // include organizations additional services but exclude the ones from other businesses
              (additionalService?.organizationId ===
                item.vehicle.organizationId &&
                !additionalService?.orgBusinessId)
            ) {
              availableOffers.push({
                ...additionalService,
                quantity: 0,
                offerPrice: service?.offerPrice,
                tax: service?.tax,
              });
            }
          }
          // handle legacy users
          else if (service) {
            availableOffers.push({
              ...additionalService,
              quantity: 0,
              offerPrice: service?.offerPrice,
              tax: service?.tax,
            });
          }
        });
      });

      // Add adHocServices separately
      for (const item of cart) {
        for (const service of item.additionalServices) {
          if (service.adHocService) {
            const existingService = availableOffers.find(
              (offer) => offer.key === service.key
            );
            if (!existingService) {
              console.log("Push adHocService: ", service);
              availableOffers.push({
                ...service,
                quantity: 0,
                id: service.key,
                tax: service?.tax,
              });
            }
          }
        }
      }

      // Merge default and user defined additional services
      setAdditionalServiceOffers(availableOffers.filter((s) => !s?._removed));
    }
  }, [cart, selectedCompany]);

  useEffect(() => {
    fetchAdditionalServiceData();
  }, [fetchAdditionalServiceData]);

  return (
    <motion.div
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageTransition}
      key="additionalServices-page"
      style={{
        paddingBottom: 30,
        backgroundColor: theme.palette.primary.backgroundColor,
        display: "flex",
        flexDirection: "column",
        margin: "10px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          alignItems: "flex-start",
          marginTop: "15px",
          position: "sticky",
          top: 0,
          bgcolor: theme.palette.primary.background,
          zIndex: 99,
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            bgcolor: theme.palette.secondary.backgroundColor,

            zIndex: 99,
          }}
        >
          {!FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE && (
            <Box
              sx={{
                flex: 0.5,
                bgcolor: theme.palette.primary.backgroundColor,
                zIndex: 99,
              }}
            >
              <Typography
                style={{
                  fontFamily: "Sofia Pro",
                  fontSize: "22px",
                  color: theme.palette.primary.mainText,
                }}
              >
                {translation.get(language, "Additional services title")}
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 0.2, minWidth: "150px" }}>
            <Button
              sx={{
                textTransform: "none",
                fontWeight: "bolder",
                letterSpacing: 1,
                fontSize: "18px",
                fontFamily: "Sofia Pro",
                backgroundColor: exportLicenseError
                  ? undefined
                  : theme.palette.secondary.main,
                color: exportLicenseError
                  ? undefined
                  : theme.palette.primary.buttonText,
              }}
              fullWidth
              onClick={() => handleAdvance()}
              disabled={cart.length === 0 || exportLicenseError}
              variant="contained"
            >
              {translation.get(language, "Continue order")}
            </Button>
          </Box>
        </Stack>
        <Divider sx={{ marginY: "10px" }} flexItem orientation="horizontal" />
      </Box>
      <Stack direction={mobileViewPort ? "column" : "row"} spacing={2} flex={1}>
        <Box flex={1}>
          <Grid container spacing={1} direction={"row"}>
            <AdditionalServiceList
              FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE={
                FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE
              }
              categories="['normal']"
              additionalServices={additionalServiceOffers}
              getQuantity={getPriceMultiplier}
              serviceQuantity={serviceQuantity}
              additionalServiceMoreInfo={additionalServiceMoreInfo}
              additionalServiceOffers={additionalServiceOffers}
              theme={theme}
              selectedAdditionalServices={selectedAdditionalServices}
              updateDetails={updateDetails}
              exportLicenseError={exportLicenseError}
            />
            <AdditionalServiceList
              FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE={
                FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE
              }
              categories="['deductible']"
              title={translation.get(language, "Insurance coverage title")}
              additionalServices={additionalServiceOffers}
              getQuantity={getPriceMultiplier}
              serviceQuantity={serviceQuantity}
              additionalServiceMoreInfo={additionalServiceMoreInfo}
              additionalServiceOffers={additionalServiceOffers}
              theme={theme}
              selectedAdditionalServices={selectedAdditionalServices}
            />

            <AdditionalServiceList
              FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE={
                FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE
              }
              categories="['distance']"
              title={translation.get(language, "Distance service title")}
              additionalServices={additionalServiceOffers}
              getQuantity={getPriceMultiplier}
              serviceQuantity={serviceQuantity}
              additionalServiceMoreInfo={additionalServiceMoreInfo}
              additionalServiceOffers={additionalServiceOffers}
              theme={theme}
            />
            {groupedDeductibles && (
              <GroupedService
                parent={groupedDeductibles.parent}
                services={groupedDeductibles.children}
                mobileViewPort={mobileViewPort}
              />
            )}
          </Grid>
        </Box>
        <PriceSummary />
      </Stack>
    </motion.div>
  );
}

function AdditionalServiceList({
  FEATURE_HIDE_ADDITIONAL_SERVICES_TITLE,
  categories,
  getQuantity,
  additionalServiceOffers,
  additionalServiceMoreInfo,
  decrementServiceQuantity,
  serviceQuantity,
  classes,
  theme,
  title,
  selectedAdditionalServices,
  updateDetails,
  exportLicenseError,
}) {
  const {
    mobileViewPort,
    language,
    FEATURE_FORCED_SERVICE,
    FEATURE_FORCED_SERVICE_IDS,
    selectedCompany,
  } = useContext(AppContext);

  const alreadySelectedDeductible = useMemo(() => {
    const deductibleAlreadySelected = selectedAdditionalServices?.find(
      (item) => item.category == "deductible"
    );
    if (deductibleAlreadySelected && categories.includes("deductible"))
      return true;
    else return false;
  }, [selectedAdditionalServices, categories]);

  return (
    <>
      {additionalServiceOffers
        .filter((s) => categories.includes(s.category))
        .sort((s1, s2) => {
          if (categories.includes("distance")) {
            try {
              const r = /\d+/g;
              const num1 = parseInt(s1.description.match(r)[0]);
              const num2 = parseInt(s2.description.match(r)[0]);
              return num1 - num2;
            } catch (e) {
              // Handle non numerical eg. "Rajattomat kilometrit"
              return -1;
            }
          } else {
            return s1.description.localeCompare(s2.description);
          }
        })
        .map((service, index) => {
          if (
            FEATURE_FORCED_SERVICE &&
            FEATURE_FORCED_SERVICE_IDS.includes(service?.id)
          ) {
            return (
              <FeatureForcedService
                key={service.id}
                service={service}
                additionalServiceMoreInfo={additionalServiceMoreInfo}
                additionalServiceOffers={additionalServiceOffers}
                getQuantity={getQuantity}
                serviceQuantity={serviceQuantity}
                updateDetails={updateDetails}
                selectedAdditionalServices={selectedAdditionalServices}
                exportLicenseError={exportLicenseError}
                decrementServiceQuantity={decrementServiceQuantity}
                FEATURE_FORCED_SERVICE_IDS={FEATURE_FORCED_SERVICE_IDS}
              />
            );
          }
          return (
            <Grid item key={service.id} xs={6} md={4}>
              <Box
                sx={{
                  //    width: "115px",
                  height: "45px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  marginBottom: "5px",
                }}
              >
                <ServiceDescription service={service} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    overflow: "hidden",
                    height: "100px",
                    width: "100px",
                    border: "solid 1px rgba(0,0,0,0.1)",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "5px",
                  }}
                >
                  <ServiceImage service={service} />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {additionalServiceMoreInfo && (
                    <Typography
                      variant="subtitle2"
                      style={{
                        color: theme.palette.primary.additionalInfo,
                        textDecoration: "underline",
                        marginBottom: 5,
                      }}
                    >
                      [+] Lue lisää
                    </Typography>
                  )}
                  <Typography
                    variant="h6"
                    align="center"
                    style={{ color: theme.palette.primary.mainText }}
                  >
                    {centsToLocalString(
                      //service.offerPrice
                      service.offerPrice * getQuantity(service),
                      true
                    )}
                  </Typography>
                </Box>
              </Box>
              <QuantitySelector
                serviceQuantity={serviceQuantity}
                service={service}
                alreadySelectedDeductible={alreadySelectedDeductible}
                selectedCompany={selectedCompany}
              />
              {serviceQuantity(service.id) !== 0 && service?.enableTextInput && (
                <Box
                  sx={{
                    display: "flex",

                    flexDirection: "column",
                    marginTop: "10px",
                    justifyContent: "space-between",
                  }}
                >
                  <FormControl>
                    <FormLabel>
                      <ServiceInputLabel service={service} />
                    </FormLabel>

                    <TextField
                      //  required={service?.type === "EXPORT_LICENSE"}
                      error={exportLicenseError}
                      helperText={
                        exportLicenseError &&
                        translation.get(language, "Fill this field label")
                      }
                      autoComplete="off"
                      defaultValue={
                        selectedAdditionalServices.find(
                          (s) => s.id === service.id
                        )?.details
                          ? selectedAdditionalServices.find(
                              (s) => s.id === service.id
                            )?.details
                          : ""
                      }
                      onChange={(e) =>
                        updateDetails(service.id, e.target.value)
                      }
                      id={service.id}
                      name="details"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{ transition: "250ms ease-in-out" }}
                          >
                            {selectedAdditionalServices.find(
                              (s) => s.id === service.id
                            )?.details && (
                              <Check
                                sx={{
                                  color: theme.palette.success.main,
                                }}
                              />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Box>
              )}
            </Grid>
          );
        })}
    </>
  );
}

function ServiceInputLabel({ service }) {
  const { language } = useContext(AppContext);
  if (service?.id === "29e009e9-9630-413e-b992-95724b4195c7") {
    return translation.get(language, "Gnista additional service label");
  }
  if (service?.inputLabel) {
    return service?.inputLabel;
  }
  return translation.get(language, "More info label");
}

function QuantitySelector(props) {
  const {
    serviceQuantity,
    service,
    alreadySelectedDeductible,
    selectedCompany,
  } = props;
  const theme = useTheme();
  const {
    selectedAdditionalServices,

    setSelectedAdditionalServices,
  } = useContext(AppContext);

  const incrementServiceQuantity = (service) => {
    // console.log("OR-46 incrementServiceQuantity");
    gtmAddServiceToCart(service, selectedCompany);
    const getService = selectedAdditionalServices.find(
      (s) => s.id === service.id
    );

    if (getService) {
      setSelectedAdditionalServices((prevState) => {
        return prevState.map((s) => {
          const adjustQuantity = () => {
            if (service?.maxQuantity && s.quantity === service?.maxQuantity) {
              return s.quantity;
            }

            if (s.enableQuantity) {
              return s.quantity + 1;
            }
            return 1;
          };

          if (s.id === getService.id) {
            // Update the quantity of the specific service
            return {
              ...s,
              quantity: adjustQuantity(),
              offerPrice: service?.offerPrice * adjustQuantity(),
            };
          }
          return s;
        });
      });
    } else {
      const serviceWithQuantity = { ...service, quantity: 1 };

      setSelectedAdditionalServices((prevState) => [
        ...prevState,
        serviceWithQuantity,
      ]);
    }
  };

  const decrementServiceQuantity = (service) => {
    // console.log("OR-46 decrementServiceQuantity");
    gtmRemoveServiceFromCart(service, selectedCompany);
    const getService = selectedAdditionalServices.find(
      (s) => s.id === service.id
    );
    if (getService?.quantity > 1) {
      setSelectedAdditionalServices((prevState) => {
        return prevState.map((s) => {
          if (s.id === getService.id) {
            // Update the quantity of the specific service
            return {
              ...s,
              quantity: s.quantity - 1,
              offerPrice: service?.offerPrice * (s?.quantity - 1),
            };
          }
          return s;
        });
      });
    }
    if (getService?.quantity === 1) {
      setSelectedAdditionalServices((prevState) => {
        return prevState.filter((s) => s.id !== getService.id);
      });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
      }}
    >
      <Button
        variant="contained"
        disableElevation
        onClick={() => decrementServiceQuantity(service)}
        sx={{
          maxWidth: "35px",
          maxHeight: "35px",
          minWidth: "35px",
          minHeight: "35px",
          backgroundColor: theme.palette.primary.decButton,
          color: theme.palette.primary.buttonText,
        }}
      >
        <Typography variant="body2" style={{ fontSize: "1,25em" }}>
          -
        </Typography>
      </Button>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "35px",
          maxHeight: "35px",
          minWidth: "35px",
          minHeight: "35px",
          fontWeight: "bolder",
          backgroundColor: theme.palette.primary.qtyBackground,
          color: theme.palette.primary.mainText,
        }}
      >
        <Typography variant="body2" style={{ fontSize: "1em" }}>
          {serviceQuantity(service.id)}
        </Typography>
      </div>
      <Button
        variant="contained"
        disableElevation
        disabled={alreadySelectedDeductible}
        sx={{
          maxWidth: "35px",
          maxHeight: "35px",
          minWidth: "35px",
          minHeight: "35px",
          fontWeight: "bold",
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0,
          backgroundColor: theme.palette.primary.addButton,
          color: theme.palette.primary.buttonText,
        }}
        onClick={() => incrementServiceQuantity(service)}
      >
        <Typography variant="body2" style={{ fontSize: "1,25em" }}>
          +
        </Typography>
      </Button>
    </div>
  );
}

function FeatureForcedService(props) {
  const {
    service,
    additionalServiceMoreInfo,
    getQuantity,
    serviceQuantity,
    updateDetails,
    selectedAdditionalServices,
    serviceDescription,
    exportLicenseError,
  } = props;
  const { language, switchForcedService, FEATURE_FORCED_SERVICE } =
    useContext(AppContext);
  const theme = useTheme();

  const selectedService = useMemo(() => {
    if (selectedAdditionalServices) {
      return selectedAdditionalServices.find((s) => s.id === service?.id);
    }
  }, [selectedAdditionalServices, service]);

  return (
    <Grid item key={service.id} xs={6} md={4}>
      <Box
        sx={{
          //    width: "115px",
          height: "45px",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
          marginBottom: "5px",
        }}
      >
        <Typography
          align="center"
          variant="body1"
          sx={{
            fontWeight: "bolder",
            color: theme.palette.primary.mainText,
          }}
        >
          {serviceDescription(service)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ServiceImage service={service} theme={theme} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {additionalServiceMoreInfo && (
            <Typography
              variant="subtitle2"
              style={{
                color: theme.palette.primary.additionalInfo,
                textDecoration: "underline",
                marginBottom: 5,
              }}
            >
              [+] Lue lisää
            </Typography>
          )}
          <Typography
            variant="h6"
            align="center"
            style={{ color: theme.palette.primary.mainText }}
          >
            {centsToLocalString(
              service.offerPrice * getQuantity(service),
              true
            )}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Switch
              size="medium"
              checked={selectedService ? true : false}
              onChange={() => switchForcedService(service)}
            />
          </Box>
        </Box>
      </Box>
      {serviceQuantity(service.id) !== 0 && service?.enableTextInput && (
        <Box
          sx={{
            display: "flex",

            flexDirection: "column",
            marginTop: "10px",
            justifyContent: "space-between",
          }}
        >
          <FormControl>
            <FormLabel>
              <ServiceInputLabel service={service} />
            </FormLabel>

            <TextField
              //  required={service?.type === "EXPORT_LICENSE"}
              error={exportLicenseError}
              helperText={
                exportLicenseError &&
                translation.get(language, "Fill this field label")
              }
              autoComplete="off"
              defaultValue={
                selectedAdditionalServices.find((s) => s.id === service?.id)
                  ?.details
                  ? selectedAdditionalServices.find((s) => s.id === service.id)
                      ?.details
                  : ""
              }
              onChange={(e) => updateDetails(service.id, e.target.value)}
              id={service.id}
              name="details"
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ transition: "250ms ease-in-out" }}
                  >
                    {selectedAdditionalServices.find((s) => s.id === service.id)
                      ?.details && (
                      <Check
                        sx={{
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Box>
      )}
    </Grid>
  );
}

const ServiceDescription = ({ service }) => {
  const { language } = useContext(AppContext);
  const description = getServiceDescription(service, language);
  const theme = useTheme();

  return (
    <Typography
      align="center"
      variant="body1"
      sx={{
        fontWeight: "bolder",
        color: theme.palette.primary.mainText,
      }}
    >
      {description}
    </Typography>
  );
};
